import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import axios, { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';

import { useMicrosoft } from '../../_libs/hooks/useMicrosoft';

import MicrosoftSSOWidget from '../../molecules/MicrosoftSSOWidget';
import StandardPageTemplate from '../../templates/StandardPage';

// NOTE: Create Subscription Context / Provider
// function handleConfirm () {}
// function handleActivation () {}
// function handleUnsubscribe () {}
// function handleChangePlan () {}
// function handleChangeQuantity () {}

function fetchSubscriptionData (subscriptionId: string, setSubscriptionData: (subscriptionData: any) => void, setContactData: (contactData: any) => void) {
  axios
    .get(`/apis/microsoft/subscriptions/${subscriptionId}/`)
    .then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK') {
          setSubscriptionData(res.data.subscription);
          setContactData(res.data.contact);
        } else {
          console.error('<SubscriberSubscriptionDetail> Error fetching subscription details for the subscription.');
        }
      },
      (err: AxiosError) => {
        console.error(`<SubscriberSubscriptionDetail> Error fetching subscription data: ${err}.`);
      },
    );
}

const PublisherSubscriptionDetailPage = (): JSX.Element => {
  const { graphData, planData, fetchPlanData, error } = useMicrosoft();
  const { subscriptionId } = useParams();
  const { 
    handleSubmit,
    register,
  } = useForm();
  const [subscriptionData, setSubscriptionData] = useState<any>();
  const [contactData, setContactData] = useState<any>();
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  useEffect(() => {
    if (graphData) {
      fetchSubscriptionData(subscriptionId, setSubscriptionData, setContactData);
      fetchPlanData(subscriptionId);
    }
  }, [graphData]);

  function handleConfirmChangePlan (values: FieldValues) {
    axios
      .post(
        '/apis/microsoft/subscriptions/update_plan/',
        {
          planId: values.newPlanId,
          subscriptionId: subscriptionData.id,
        },
      )
      .then(
        () => {
          // TODO: Handle success state
        },
        (reason: any) => {
          console.error(reason);
        },
      );
  }

  function handleConfirmCancelSubscription () {
    axios
      .post(
        '/apis/microsoft/subscriptions/unsubscribe/',
        {
          subscriptionId: subscriptionData.id,
        },
      )
      .then(
        () => {
          // TODO: Handle success
        },
        (reason:any) => {
          console.error(reason);
        },
      );
  }

  function handleActivateSubscription () {
    axios
      .post('/apis/microsoft/subscriptions/activate/',
        {
          planId: subscriptionData.planId,
          subscriptionId: subscriptionData.id,
        },
      )
      .then(
        () => {
          // TODO: Handle success
        },
        (reason: any) => {
          console.error(reason);
        },
      );
  }

  let pageContent = <></>;
  if (!graphData) {
    pageContent = <MicrosoftSSOWidget error={error} type={'subscriber'} />;
  }
  else if (contactData && subscriptionData && planData) {
    const planContent = _.find(planData, {
      planId: subscriptionData.planId,
    });
    const planSelector = (
      <form onSubmit={handleSubmit(handleConfirmChangePlan)}>
        <select {...register('newPlanId')}>
          {
            _.map(planData, (plan, i) => {
              return (
                <option
                  value={plan.planId}
                  key={i}
                >
                  {plan.displayName}{plan.description ? ` - ${plan.description}` : ''}
                </option>
              );
            })
          }
        </select>
        <button
          type="submit"
        >
          Confirm Change
        </button>
        <button
          type="button"
          onClick={() => { setIsChangingPlan(false); }}
        >
          Cancel Changing Plan
        </button>
      </form>
    );
    const confirmCancel = (
      <div>
        <em>Are you sure? This action cannot be undone.</em>
        <button type="submit" onClick={handleConfirmCancelSubscription}>Confirm Unsubscribe</button>
        <button type="submit" onClick={ () => { setIsConfirmingCancel(false); } }>Cancel Unsubscribe</button>
      </div>
    );
    pageContent = (
      <div>
        <div>Subscription ID: {subscriptionData.id}</div>
        <div>Name: {subscriptionData.name}</div>
        <div>Plan: {planContent.displayName}</div>
        <div>Plan Description: {planContent.description}</div>
        <div>Quantity: {subscriptionData.quantity}</div>
        <div>Start date: {dayjs(subscriptionData.term.startDate).format('M/DD/YY H:mm A')}</div>
        <div>End date: {dayjs(subscriptionData.term.endDate).format('M/DD/YY H:mm A')}</div>
        <div>Subscription term: {subscriptionData.term.termUnit}</div>
        <div>Last modified: {dayjs(subscriptionData.lastModified).format('M/DD/YY H:mm A')}</div>
        <div>Status: {subscriptionData.saasSubscriptionStatus}</div>
        <div>Email: {contactData.email}</div>
        <div>Phone: {contactData.phone}</div>
        {
          (subscriptionData.saasSubscriptionStatus === 'Subscribed') &&
            <div>
              { !isChangingPlan && 
                  <button
                    type="button"
                    className="mr-2"
                    onClick={() => { setIsChangingPlan(true); }}
                  >
                    Change Plan
                  </button>
              }
              { isChangingPlan && planSelector }
              {
                !isConfirmingCancel &&
                  <button
                    type="button"
                    onClick={() => { setIsConfirmingCancel(true); }}
                  >
                    Cancel Subscription
                  </button>
              }
              {isConfirmingCancel && confirmCancel }
            </div>
        }
        {
          (subscriptionData.saasSubscriptionStatus === 'PendingFulfillmentStart') &&
            <div>
              <button
                type="button"
                onClick={handleActivateSubscription}
              >
                Activate Subscription
              </button>
            </div>
        }
      </div>
    );
  } else {
    pageContent = (
      <p>
        Subscription data is loading...
      </p>
    );
  }
  return (
    <StandardPageTemplate>
      <h1 className="f-serif f-normal f-italic">
        Publisher Subscription Details
      </h1>
      <div>
        {pageContent}
      </div>
      {
        graphData
         &&
          <div>
            <Link className="button td-none" to="/microsoft/publisher/subscriptions/">View All Subscriptions</Link>
          </div>
      }
    </StandardPageTemplate>
  );
};

export default PublisherSubscriptionDetailPage;
